import React, { useEffect, useState, Suspense, lazy, createElement } from 'react';
import { initFbPixel, trackFbEvent, trackSectionViews, trackScrollDepth, trackTimeOnPage } from './utils/fbPixel';
// Lazy-load EVERYTHING below the hero — only the hero is in the initial bundle.
// This includes SocialProof, which is below the fold on most mobile devices.
const SocialProof = lazy(() => import('./components/SocialProof').then(m => ({
  default: m.SocialProof
})));
const PainPoints = lazy(() => import('./components/PainPoints').then(m => ({
  default: m.PainPoints
})));
const ProductShowcase = lazy(() => import('./components/ProductShowcase').then(m => ({
  default: m.ProductShowcase
})));
const WhatYouGet = lazy(() => import('./components/WhatYouGet').then(m => ({
  default: m.WhatYouGet
})));
const Bonuses = lazy(() => import('./components/Bonuses').then(m => ({
  default: m.Bonuses
})));
const Countdown = lazy(() => import('./components/Countdown').then(m => ({
  default: m.Countdown
})));
const Pricing = lazy(() => import('./components/Pricing').then(m => ({
  default: m.Pricing
})));
const ProBusiness = lazy(() => import('./components/ProBusiness').then(m => ({
  default: m.ProBusiness
})));
const Guarantee = lazy(() => import('./components/Guarantee').then(m => ({
  default: m.Guarantee
})));
const Testimonials = lazy(() => import('./components/Testimonials').then(m => ({
  default: m.Testimonials
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const DarkCTA = lazy(() => import('./components/DarkCTA').then(m => ({
  default: m.DarkCTA
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const PurchaseNotifications = lazy(() => import('./components/PurchaseNotifications').then(m => ({
  default: m.PurchaseNotifications
})));
// Hero image — responsive variants for LCP. Conservative sizes so retina mobile
// doesn't fetch oversized assets. MUST match the preload in index.tsx exactly.
const HERO_BASE = 'https://res.cloudinary.com/dowzlbqlz/image/upload/f_auto,q_auto';
const HERO_PATH = '/v1777816369/hf_20260503_134623_1bb422cd-84e7-4ec9-9a58-4fe2f3159cdc-Photoroom_d73vml.webp';
const HERO_SRC = `${HERO_BASE},w_320${HERO_PATH}`;
const HERO_SRCSET = `${HERO_BASE},w_240${HERO_PATH} 240w, ${HERO_BASE},w_320${HERO_PATH} 320w, ${HERO_BASE},w_480${HERO_PATH} 480w`;
const OG_IMG = 'https://cdn.magicpatterns.com/uploads/9uMhQHg3RXXbKPqANDF9n5/hf_20260423_164447_343907b5-95aa-49ff-ba71-eeea9d0be6f6-Photoroom.webp';
const TAGS = ['📄 100% PDF', '⚡ Acceso Inmediato', '🇦🇷 Argentina', '🎓 Toda la Carrera', '✅ Sistema Listo', '📚 6 Tomos + 6 Bonos', '🗂️ Versión con Editables'];
// Placeholder while lazy chunks load — avoids layout shift
const SectionFallback = () => <div style={{
  minHeight: 200
}} data-id="element-0" />;
// Prefetch lazy chunks during browser idle time so they're cached
// by the time the user scrolls to them. Called once after first paint.
function prefetchChunks() {
  // Fire and forget — these promises are intentionally not awaited
  void import('./components/SocialProof');
  void import('./components/PainPoints');
  void import('./components/ProductShowcase');
  void import('./components/WhatYouGet');
  void import('./components/Bonuses');
  void import('./components/Countdown');
  void import('./components/Pricing');
  void import('./components/ProBusiness');
  void import('./components/Guarantee');
  void import('./components/Testimonials');
  void import('./components/FAQ');
  void import('./components/DarkCTA');
  void import('./components/Footer');
}
export function App() {
  // Gate the floating purchase-notifications widget until the user actually
  // interacts with the page — saves JS execution + a network chunk on first load.
  const [showNotifications, setShowNotifications] = useState(false);
  // SEO meta tags — deferred to idle so they never block LCP
  useEffect(() => {
    const runSeo = () => {
      document.title = 'Megapack Kinesiología: Carrera Resuelta — El Sistema Completo para Estudiantes de Kinesiología en Argentina';
      document.documentElement.lang = 'es';
      const setMeta = (name: string, content: string, property?: boolean) => {
        const attr = property ? 'property' : 'name';
        let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute(attr, name);
          document.head.appendChild(el);
        }
        el.content = content;
      };
      const description = 'El sistema completo de estudio para estudiantes de Kinesiología en Argentina. 6 Tomos de 60 páginas cada uno con todo el contenido de la carrera organizado, más 6 bonos exclusivos. También disponible en una versión más completa con kit editable de organización académica. Acceso inmediato.';
      const url = 'https://kinesiologiacarreraresuelta.com';
      const image = OG_IMG;
      setMeta('description', description);
      setMeta('keywords', 'megapack kinesiología, carrera resuelta, estudiar kinesiología argentina, material kinesiología universitario, biomecánica argentina, morfofisiología kinesiología, casos clínicos kinesiología, guía práctica hospitalaria, técnicas manuales argentina, apuntes kinesiología UBA UNLP UNC');
      setMeta('robots', 'index, follow');
      setMeta('author', 'Megapack Kinesiología: Carrera Resuelta');
      setMeta('viewport', 'width=device-width, initial-scale=1');
      if (!document.querySelector('link[rel="canonical"]')) {
        const canonical = document.createElement('link');
        canonical.rel = 'canonical';
        canonical.href = url;
        document.head.appendChild(canonical);
      }
      setMeta('og:title', 'Megapack Kinesiología: Carrera Resuelta — El Sistema Completo para Estudiantes de Kinesiología en Argentina', true);
      setMeta('og:description', description, true);
      setMeta('og:image', image, true);
      setMeta('og:url', url, true);
      setMeta('og:type', 'product', true);
      setMeta('og:locale', 'es_AR', true);
      setMeta('og:site_name', 'Megapack Kinesiología: Carrera Resuelta™', true);
      setMeta('twitter:card', 'summary_large_image');
      setMeta('twitter:title', 'Megapack Kinesiología: Carrera Resuelta — El Sistema Completo para Estudiantes de Kinesiología');
      setMeta('twitter:description', description);
      setMeta('twitter:image', image);
      if (!document.querySelector('script[data-ld="product"]')) {
        const ld = document.createElement('script');
        ld.type = 'application/ld+json';
        ld.setAttribute('data-ld', 'product');
        ld.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'Megapack Kinesiología: Carrera Resuelta™',
          description,
          image,
          brand: {
            '@type': 'Brand',
            name: 'Megapack Kinesiología: Carrera Resuelta™'
          },
          offers: {
            '@type': 'AggregateOffer',
            lowPrice: '22990',
            highPrice: '29990',
            priceCurrency: 'ARS',
            availability: 'https://schema.org/InStock',
            offerCount: 2
          },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.9',
            reviewCount: '512'
          }
        });
        document.head.appendChild(ld);
      }
      if (!document.querySelector('script[data-ld="org"]')) {
        const orgLd = document.createElement('script');
        orgLd.type = 'application/ld+json';
        orgLd.setAttribute('data-ld', 'org');
        orgLd.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Megapack Kinesiología: Carrera Resuelta™',
          url,
          logo: image,
          email: 'hola@kinesiologiacarreraresuelta.com',
          sameAs: ['https://www.instagram.com/kinesiologiacarreraresuelta']
        });
        document.head.appendChild(orgLd);
      }
      if (!document.querySelector('script[data-ld="faq"]')) {
        const faqLd = document.createElement('script');
        faqLd.type = 'application/ld+json';
        faqLd.setAttribute('data-ld', 'faq');
        faqLd.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [{
            '@type': 'Question',
            name: '¿Qué incluye el Megapack Kinesiología: Carrera Resuelta?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'El MegaPack Base incluye 6 Tomos de 60 páginas cada uno con todo el contenido de la carrera de Kinesiología organizado por área, más 6 bonos exclusivos. También está disponible una versión más completa con kit editable de organización académica.'
            }
          }, {
            '@type': 'Question',
            name: '¿Cómo recibo el material?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Todo es 100% digital en formato PDF. Recibís acceso inmediato en tu email dentro de los 5 minutos posteriores a la compra.'
            }
          }, {
            '@type': 'Question',
            name: '¿Tiene garantía?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Sí, ofrecemos garantía de 7 días sin preguntas. Si no te sirve, te devolvemos el 100% de tu dinero.'
            }
          }, {
            '@type': 'Question',
            name: '¿Está adaptado a Argentina?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Sí, 100%. Todo el contenido está adaptado al plan de estudios argentino, con terminología, escalas y protocolos utilizados en las universidades nacionales.'
            }
          }, {
            '@type': 'Question',
            name: '¿Para qué año de la carrera está pensado?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Para toda la carrera. El Tomo 1 cubre Morfofisiología, los Tomos 2 al 5 cubren el núcleo clínico y el Tomo 6 cubre la práctica hospitalaria y la salida laboral.'
            }
          }]
        });
        document.head.appendChild(faqLd);
      }
    };
    if ('requestIdleCallback' in window) {
      ;
      (window as any).requestIdleCallback(runSeo, {
        timeout: 2000
      });
    } else {
      setTimeout(runSeo, 200);
    }
  }, []);
  // Pixel + section/scroll/time tracking — consolidated to one effect
  useEffect(() => {
    initFbPixel();
    const cleanupSections = trackSectionViews({
      '[data-section="hero"]': 'Hero',
      '[data-section="social-proof"]': 'Social Proof',
      '[data-section="pain-points"]': 'Pain Points',
      '[data-section="product-showcase"]': 'Product Showcase',
      '[data-section="what-you-get"]': 'What You Get',
      '[data-section="bonuses"]': 'Bonuses',
      '[data-section="countdown"]': 'Countdown / Urgency',
      '#pricing': 'Pricing',
      '[data-section="pro-business"]': 'Máster Kinesiología',
      '[data-section="guarantee"]': 'Guarantee',
      '[data-section="testimonials"]': 'Testimonials',
      '[data-section="faq"]': 'FAQ',
      '[data-section="dark-cta"]': 'Final CTA',
      '[data-section="footer"]': 'Footer'
    });
    const cleanupScroll = trackScrollDepth();
    const cleanupTime = trackTimeOnPage();
    return () => {
      cleanupSections();
      cleanupScroll();
      cleanupTime();
    };
  }, []);
  // Prefetch lazy chunks during idle time + mount the floating notifications
  // only after first user interaction. Both keep main-thread free at startup.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const idle = (cb: () => void, timeout = 2500) => {
      if ('requestIdleCallback' in window) {
        ;
        (window as any).requestIdleCallback(cb, {
          timeout
        });
      } else {
        setTimeout(cb, 1500);
      }
    };
    // Prefetch all below-the-fold chunks once the main thread is idle
    idle(prefetchChunks, 3000);
    // Wait for first interaction before mounting the notifications widget
    const trigger = () => {
      cleanup();
      setShowNotifications(true);
    };
    const events: Array<keyof WindowEventMap> = ['scroll', 'touchstart', 'mousemove', 'keydown', 'click'];
    const cleanup = () => {
      events.forEach(e => window.removeEventListener(e, trigger as EventListener));
    };
    events.forEach(e => window.addEventListener(e, trigger as EventListener, {
      once: true,
      passive: true
    } as AddEventListenerOptions));
    // Fallback: mount after 6s even if there's no interaction (e.g. bot)
    const fallback = setTimeout(() => {
      cleanup();
      setShowNotifications(true);
    }, 6000);
    return () => {
      cleanup();
      clearTimeout(fallback);
    };
  }, []);
  return <>
      <noscript data-id="element-1">
        <img height="1" width="1" style={{
        display: 'none'
      }} src="https://www.facebook.com/tr?id=1215304437119773&ev=PageView&noscript=1" alt="" data-id="element-2" />
      </noscript>

      <header data-section="hero" className="relative flex w-full flex-col items-center justify-center text-center text-gray-900 text-base leading-6 px-5 sm:px-6 py-12 sm:py-16" role="banner" style={{
      backgroundImage: 'linear-gradient(180deg, rgb(219,234,254) 0%, rgb(239,246,255) 50%, rgb(219,234,254) 100%)',
      backgroundColor: 'rgb(219,234,254)'
    }} data-id="element-3">
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-blue-400/30 bg-white/80 px-4 py-1.5 shadow-sm" data-id="element-4">
          <span className="text-xs sm:text-sm font-bold text-blue-700 tracking-wide uppercase" data-id="element-5">
            🎓 Sistema de Estudio para Kinesiología · Estudiantes Argentina
          </span>
        </div>

        <h1 className="sm:text-5xl md:text-[72px] sm:leading-snug md:leading-[88px] font-black max-w-4xl text-gray-900 m-0 mb-2 tracking-tight pt-[10px] pb-[10px] text-[23px]" data-id="element-6">
          Toda la carrera de Kinesiología
          <br data-id="element-7" />
          en un solo lugar.
          <br data-id="element-8" />
          <span className="text-blue-600 mt-1 inline-block" data-id="element-9">
            Sin buscar más, sin improvisar.
          </span>
        </h1>

        <div className="my-6 sm:my-8 w-full max-w-[320px] sm:max-w-[380px] aspect-square relative" data-id="element-10">
          <div className="absolute inset-4 rounded-full z-0 pointer-events-none" style={{
          background: 'radial-gradient(circle, rgba(59,130,246,0.30) 0%, rgba(37,99,235,0.10) 50%, transparent 70%)'
        }} data-id="element-11" />
          <div className="relative z-10 w-full h-full" data-id="element-12">
            <img src={HERO_SRC} srcSet={HERO_SRCSET} sizes="(max-width: 640px) 280px, 380px" alt="Megapack Kinesiología: Carrera Resuelta — Sistema Completo para Estudiantes de Kinesiología Argentina" width="400" height="400" fetchPriority="high" decoding="async" className="block max-w-full w-full h-auto object-contain" data-id="element-13" />
          </div>
        </div>

        <p className="max-w-xl text-base sm:text-lg font-semibold text-gray-700 leading-7 m-0 mb-4" data-id="element-14">
          El sistema que los mejores estudiantes de Kinesiología usan para
          aprobar materias, dominar la clínica, sobrevivir las prácticas
          hospitalarias y terminar la carrera{' '}
          <span className="text-gray-900 font-black" data-id="element-15">
            sin depender de apuntes incompletos ni de la suerte en los finales.
          </span>
        </p>

        <p className="max-w-xl text-lg sm:text-xl font-black text-blue-600 m-0 mb-6" data-id="element-16">
          "360 páginas de Kinesiología organizadas, listas y aplicables. Del
          primer parcial al título." Mas la versión completa con kit editable de
          organización académica.
        </p>

        <div className="mb-7 flex max-w-lg flex-wrap items-center justify-center gap-2" data-id="element-17">
          {TAGS.map(tag => <span key={tag} className="whitespace-nowrap bg-white/80 text-xs sm:text-sm font-semibold text-gray-900 leading-5 border border-blue-400/30 rounded-full px-3 py-1 shadow-sm" data-id="element-18">
              {tag}
            </span>)}
        </div>

        <a href="#pricing" onClick={() => {
        trackFbEvent('Lead', {
          content_name: 'Hero CTA Click',
          content_category: 'Kinesiología Carrera Resuelta'
        });
      }} className="block w-full max-w-sm text-white text-lg sm:text-xl font-black rounded-xl px-6 sm:px-8 py-4 mb-4 no-underline text-center shadow-lg hover:opacity-90 transition-opacity" style={{
        background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 50%, #2563eb 100%)'
      }} data-id="element-19">
          🎓 → QUIERO MI MEGAPACK AHORA
        </a>

        <div className="flex flex-row flex-wrap items-center justify-center gap-3 sm:gap-4 m-0" data-id="element-20">
          <span className="inline-flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-gray-600 shadow-sm" data-id="element-21">
            🛡️ Garantía 7 días
          </span>
          <span className="inline-flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-gray-600 shadow-sm" data-id="element-22">
            🔒 Pago seguro
          </span>
          <span className="inline-flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-gray-600 shadow-sm" data-id="element-23">
            💳 Mercado Pago | Tarjeta | Transferencia
          </span>
        </div>
      </header>

      <main data-id="element-24">
        {/* Everything below the hero is lazy-loaded */}
        <Suspense fallback={<SectionFallback data-id="element-26" />} data-id="element-25">
          <div data-section="social-proof" data-id="element-27">
            <SocialProof data-id="element-28" />
          </div>
          <div data-section="pain-points" data-id="element-29">
            <PainPoints data-id="element-30" />
          </div>
          <div data-section="product-showcase" data-id="element-31">
            <ProductShowcase data-id="element-32" />
          </div>
          <div data-section="what-you-get" data-id="element-33">
            <WhatYouGet data-id="element-34" />
          </div>
          <div data-section="bonuses" data-id="element-35">
            <Bonuses data-id="element-36" />
          </div>
          <div data-section="countdown" data-id="element-37">
            <Countdown data-id="element-38" />
          </div>
          <Pricing data-id="element-39" />
          <div data-section="pro-business" data-id="element-40">
            <ProBusiness data-id="element-41" />
          </div>
          <div data-section="guarantee" data-id="element-42">
            <Guarantee data-id="element-43" />
          </div>
          <div data-section="testimonials" data-id="element-44">
            <Testimonials data-id="element-45" />
          </div>
          <div data-section="faq" data-id="element-46">
            <FAQ data-id="element-47" />
          </div>
          <div data-section="dark-cta" data-id="element-48">
            <DarkCTA data-id="element-49" />
          </div>
        </Suspense>
      </main>

      <Suspense fallback={null} data-id="element-50">
        <div data-section="footer" data-id="element-51">
          <Footer data-id="element-52" />
        </div>
        {showNotifications && <PurchaseNotifications data-id="element-53" />}
      </Suspense>
    </>;
}